import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { updateFamilyAddress, fetchFamilyById } from "../../service/FamilyApi";
import { useForm } from "react-hook-form"; // Importing react-hook-form
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Breadcrumbs } from "../../components/shared"; // Assuming Navbar and Footer are shared components
import { Loading } from "../../components/shared";
import { useCheckAccess } from "../../utils/Utilities";
import AddressSelection from "../address/AddressSelection"; // Import AddressSelection component
import { showErrorToast, } from "../../utils/Toasts";

function FamilyAddressUpdate() {
  useCheckAccess('FamilyRW');
  const { id } = useParams();
  const navigate = useNavigate();
  const [familyData, setFamilyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addressOption, setAddressOption] = useState("both");
  const [addressData, setAddressData] = useState({
    temp: { areaId: "", subareaId: "" },
    permanent: { areaId: "", subareaId: "" },
  });

  // React Hook Form setup
  const { register, handleSubmit, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    const fetchFamilyData = async () => {
      try {
        if (id) {
          const data = await fetchFamilyById(id); // Await the fetchFamilyById function
          setFamilyData(data); // Set the family data in state
        }
      } catch (error) {
        showErrorToast("Error fetching family data");
      }
    };

    fetchFamilyData(); // Call the async function inside useEffect
  }, [id]); // Only re-run if id changes

  // When family data is updated, extract family member Aadhar numbers
  useEffect(() => {
    if (familyData && familyData.family) {
      setValue("id", String(familyData.family.id));
      setValue("kootamName", familyData.family.kootamName);
      setValue("customGroup", familyData.family.customGroup || "");
      setValue("familyHeadAadhar", familyData.family.familyHeadAadhar);
      setValue("status", familyData.family.status);

      if (familyData.family.tempAddress) {
        setAddressData(prevData => ({
          ...prevData,
          temp: {
            ...prevData.temp,
            ...familyData.family.tempAddress,
          }
        }));
      }

      if (familyData.family.permanentAddress) {
        setAddressData(prevData => ({
          ...prevData,
          permanent: {
            ...prevData.permanent,
            ...familyData.family.permanentAddress,
          }
        }));
      }
    }
  }, [familyData, setValue]);
  const onSubmit = async (data) => {
    if (loading) return;
    if (!addressData.permanent.areaId || !addressData.permanent.subareaId) {
      alert("தயவுசெய்து நிரந்தர முகவரியை நிரப்பவும்.");
      return; // Prevent form submission if validation fails
    }
    setLoading(true);
    // Prepare the payload with the updated address details
    const payload = {
      id: familyData.family.id, // Add familyId to the payload
      tempAddressLine1: (data.tempAddressLine1?.trim() || ""), // Safely trim if not null/undefined
      tempAddressLine2: (data.tempAddressLine2?.trim() || ""),
      tempAddressLine3: (data.tempAddressLine3?.trim() || ""),
      tempAddressLine1_tamil: (data.tempAddressLine1?.trim() || ""),
      tempAddressLine2_tamil: (data.tempAddressLine2?.trim() || ""),
      tempAddressLine3_tamil: (data.tempAddressLine3?.trim() || ""),
      tempAddressAreaId: addressData.temp.areaId || "", // Default to empty string if undefined
      tempSubareaId: addressData.temp.subareaId || "", // Default to empty string if undefined
      permanentAddressLine1: (data.permanentAddressLine1?.trim() || ""),
      permanentAddressLine2: (data.permanentAddressLine2?.trim() || ""),
      permanentAddressLine3: (data.permanentAddressLine3?.trim() || ""),
      permanentAddressLine1_tamil: (data.permanentAddressLine1?.trim() || ""),
      permanentAddressLine2_tamil: (data.permanentAddressLine2?.trim() || ""),
      permanentAddressLine3_tamil: (data.permanentAddressLine3?.trim() || ""),
      permanentAddressAreaId: addressData.permanent.areaId || "", // Default to empty string if undefined
      permanentSubareaId: addressData.permanent.subareaId || "", // Default to empty string if undefined
    };


    try {
      const success = await updateFamilyAddress(payload, id);
      if (success) {
        navigate(`/family/view/${id}`);
      }
    } catch (error) {
      console.error("Error updating address:", error);
      showErrorToast("Error updating address");
    } finally {
      setLoading(false);
    }
  };

  // Handle address change
  const handleAddressChange = (
    addressType,
    areaId,
    subareaId,
  ) => {
    setAddressData((prevData) => ({
      ...prevData,
      [addressType]: {
        areaId,
        subareaId,
      },
    }));
  };


  if (!familyData) {
    return (
      <>
        <Navbar />
        <div className="general_parent">
          <div className="general_container">
            <div className="general_body">
              <Breadcrumbs /><br />
              <Loading />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Helmet>
        <title>குடும்ப முகவரியைத் திருத்து</title>
        <link rel="canonical" href={`/family/address-update`} />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs /><br />
            <h3>குடும்ப முகவரியைத் திருத்து-குடும்ப எண்: {familyData.family.id}</h3>
            <br />
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
              <input
                type="hidden"
                {...register("id")}
                value={familyData.family.id}
              />
              <div className="details-card">
                <div className="detail-item">
                  <span className="detail-label"><strong>தற்காலிக முகவரி  :</strong></span>

                  {/* Check for tempAddressLine1, tempAddressLine2, tempAddressLine3 */}
                  <span>
                    {familyData?.family?.tempAddressLine1 && familyData.family.tempAddressLine1 + ','}
                    {familyData?.family?.tempAddressLine2 && familyData.family.tempAddressLine2 + ','}
                    {familyData?.family?.tempAddressLine3 && familyData.family.tempAddressLine3}
                  </span>

                  {/* Check for address data (name, block, district, state, country) */}
                  <span>
                    {familyData?.family?.addressData?.tempAddress?.name && familyData.family.addressData.tempAddress.name + ','}
                    {familyData?.family?.addressData?.tempAddress?.block && familyData.family.addressData.tempAddress.block + ','}
                    {familyData?.family?.addressData?.tempAddress?.district && familyData.family.addressData.tempAddress.district + ','}
                    {familyData?.family?.addressData?.tempAddress?.state && familyData.family.addressData.tempAddress.state + ','}
                    {familyData?.family?.addressData?.tempAddress?.country && familyData.family.addressData.tempAddress.country}
                  </span>
                  <br />

                  {/* Check for Tamil address lines */}
                  <span>
                    {familyData?.family?.tempAddressLine1_tamil && familyData.family.tempAddressLine1_tamil + ','}
                    {familyData?.family?.tempAddressLine2_tamil && familyData.family.tempAddressLine2_tamil + ','}
                    {familyData?.family?.tempAddressLine3_tamil && familyData.family.tempAddressLine3_tamil}

                  </span>

                  {/* Check for Tamil address data */}
                  <span>
                    {familyData?.family?.addressData?.tempAddress?.name_tamil && familyData.family.addressData.tempAddress.name_tamil + ','}
                    {familyData?.family?.addressData?.tempAddress?.block_tamil && familyData.family.addressData.tempAddress.block_tamil + ','}
                    {familyData?.family?.addressData?.tempAddress?.district_tamil && familyData.family.addressData.tempAddress.district_tamil + ','}
                    {familyData?.family?.addressData?.tempAddress?.state_tamil && familyData.family.addressData.tempAddress.state_tamil + ','}
                    {familyData?.family?.addressData?.tempAddress?.country_tamil && familyData.family.addressData.tempAddress.country_tamil}
                  </span>

                  {/* Check for pincode */}
                  <span>
                    {familyData?.family?.addressData?.tempAddress?.pincode && familyData.family.addressData.tempAddress.pincode}
                  </span>
                </div>

                <br />

                <div className="detail-item">
                  <span className="detail-label"><strong>நிரந்தர முகவரி:</strong></span>

                  {/* Check for permanentAddressLine1, permanentAddressLine2, permanentAddressLine3 */}
                  <span>
                    {familyData?.family?.permanentAddressLine1 && familyData.family.permanentAddressLine1 + ','}
                    {familyData?.family?.permanentAddressLine2 && familyData.family.permanentAddressLine2 + ','}
                    {familyData?.family?.permanentAddressLine3 && familyData.family.permanentAddressLine3}
                  </span>

                  {/* Check for address data (name, block, district, state, country) */}
                  <span>
                    {familyData?.family?.addressData?.permanentAddress?.name && familyData.family.addressData.permanentAddress.name + ','}
                    {familyData?.family?.addressData?.permanentAddress?.block && familyData.family.addressData.permanentAddress.block + ','}
                    {familyData?.family?.addressData?.permanentAddress?.district && familyData.family.addressData.permanentAddress.district + ','}
                    {familyData?.family?.addressData?.permanentAddress?.state && familyData.family.addressData.permanentAddress.state + ','}
                    {familyData?.family?.addressData?.permanentAddress?.country && familyData.family.addressData.permanentAddress.country}
                  </span>
                  <br />

                  {/* Check for Tamil permanent address lines */}
                  <span>
                    {familyData?.family?.permanentAddressLine1_tamil && familyData.family.permanentAddressLine1_tamil + ','}
                    {familyData?.family?.permanentAddressLine2_tamil && familyData.family.permanentAddressLine2_tamil + ','}
                    {familyData?.family?.permanentAddressLine3_tamil && familyData.family.permanentAddressLine3_tamil}

                  </span>

                  {/* Check for Tamil permanent address data */}
                  <span>
                    {familyData?.family?.addressData?.permanentAddress?.name_tamil && familyData.family.addressData.permanentAddress.name_tamil + ','}
                    {familyData?.family?.addressData?.permanentAddress?.block_tamil && familyData.family.addressData.permanentAddress.block_tamil + ','}
                    {familyData?.family?.addressData?.permanentAddress?.district_tamil && familyData.family.addressData.permanentAddress.district_tamil + ','}
                    {familyData?.family?.addressData?.permanentAddress?.state_tamil && familyData.family.addressData.permanentAddress.state_tamil + ','}
                    {familyData?.family?.addressData?.permanentAddress?.country_tamil && familyData.family.addressData.permanentAddress.country_tamil}
                  </span>

                  {/* Check for pincode */}
                  <span>
                    {familyData?.family?.addressData?.permanentAddress?.pincode && familyData.family.addressData.permanentAddress.pincode}
                  </span>
                </div>
                {/* Address Option Selection */}
                <div className="auth_element">
                  <label className="auth_label">முகவரி வகையைத் தேர்வுசெய்யவும்</label>
                  <br />
                  <div className="auth_radio">
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        name="addressOption"
                        value="both"
                        checked={addressOption === "both"}
                        onChange={() => setAddressOption("both")}
                      />
                      தற்காலிக மற்றும் நிரந்தர முகவரிகள்
                    </label>
                    <br />
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        name="addressOption"
                        value="permanent"
                        checked={addressOption === "permanent"}
                        onChange={() => setAddressOption("permanent")}
                      />
                      நிரந்தர முகவரி மட்டும்
                    </label>
                  </div>
                </div>

                {/* Temporary Address Selection (Only show if "Both" is selected) */}
                {addressOption === "both" && (
                  <>
                    <AddressSelection addressType="temp" onAddressChange={handleAddressChange} />
                    {/* Address Line Inputs */}
                    {addressData.temp.subareaId && (
                      <>
                        <div className="auth_element">
                          <label className="auth_label">தற்காலிக முகவரி வரி 1</label>
                          <input
                            type="text"
                            id="tempAddressLine1"
                            value={addressData.temp.addressLine1}
                            {...register("tempAddressLine1")}
                            className="auth_input"
                          />
                          {errors.tempAddressLine1 && <div className="auth_error">{errors.tempAddressLine1.message}</div>}
                        </div>

                        <div className="auth_element">
                          <label className="auth_label">தற்காலிக முகவரி வரி 2</label>
                          <input
                            type="text"
                            id="tempAddressLine2"
                            value={addressData.temp.addressLine2}
                            {...register("tempAddressLine2")}
                            className="auth_input"

                          />
                          {errors.tempAddressLine2 && <div className="auth_error">{errors.tempAddressLine2.message}</div>}
                        </div>

                        <div className="auth_element">
                          <label className="auth_label">தற்காலிக முகவரி வரி 3</label>
                          <input
                            type="text"
                            id="tempAddressLine3"
                            value={addressData.temp.addressLine3}
                            {...register("tempAddressLine3")}
                            className="auth_input"
                          />
                          {errors.tempAddressLine3 && <div className="auth_error">{errors.tempAddressLine3.message}</div>}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Permanent Address Selection */}
                <>
                  <AddressSelection addressType="permanent" onAddressChange={handleAddressChange} />
                  {/* Address Line Inputs */}
                  {addressData.permanent.subareaId && (
                    <>
                      <div className="auth_element">
                        <label className="auth_label">நிரந்தர முகவரி வரி 1</label>
                        <input
                          type="text"
                          id="permanentAddressLine1"
                          value={addressData.permanent.addressLine1}
                          {...register("permanentAddressLine1")}
                          className="auth_input"

                        />
                        {errors.permanentAddressLine1 && <div className="auth_error">{errors.permanentAddressLine1.message}</div>}
                      </div>
                      <div className="auth_element">
                        <label className="auth_label">நிரந்தர முகவரி வரி 2</label>
                        <input
                          type="text"
                          id="permanentAddressLine2"
                          value={addressData.permanent.addressLine2}
                          {...register("permanentAddressLine2")}
                          className="auth_input"

                        />
                        {errors.permanentAddressLine2 && <div className="auth_error">{errors.permanentAddressLine2.message}</div>}
                      </div>
                      <div className="auth_element">
                        <label className="auth_label">நிரந்தர முகவரி வரி 3</label>
                        <input
                          type="text"
                          id="permanentAddressLine3"
                          value={addressData.permanent.addressLine3}
                          {...register("permanentAddressLine3")}
                          className="auth_input"
                        />
                        {errors.permanentAddressLine3 && <div className="auth_error">{errors.permanentAddressLine3.message}</div>}
                      </div>
                    </>
                  )}
                </>

                <button type="submit" className="auth_submit">முகவரியைப் புதுப்பிக்கவும் </button>

                <br />

              </div>
            </form>
            <br />
            <button onClick={() => navigate(`/family/view/${id}`)}>குடும்ப விவரங்கள்</button>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default FamilyAddressUpdate;
